import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { discountService } from 'corporate/services';
import { IUpsertPricingProfileCostingCalculationPayload } from 'pricing/types';

interface IProps {
  selectionCount?: number;
  position?: NotificationPosition;
}

export const useCostingCalculationActions = ({ selectionCount, position }: IProps = {}) => {
  return {
    ...useUpsertCostingCalculation(),
    ...useRemoveCostingCalculations(selectionCount, position),
  };
};

const useUpsertCostingCalculation = () => {
  const { errors } = useHandleErrors({
    success: (payload: IUpsertPricingProfileCostingCalculationPayload) => ({
      message: payload?.id
        ? t('messageCostingCalculationUpdateSuccess')
        : t('messageCostingCalculationCreateSuccess'),
    }),
    loading: (payload: IUpsertPricingProfileCostingCalculationPayload) => ({
      message: payload?.id
        ? t('messageCostingCalculationUpdateInProgress')
        : t('messageCostingCalculationCreateInProgress'),
    }),
    error: (payload: IUpsertPricingProfileCostingCalculationPayload) => ({
      title: payload?.id
        ? t('errorTitleCostingCalculationUpdate')
        : t('errorTitleCostingCalculationCreate'),
    }),
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    (payload: IUpsertPricingProfileCostingCalculationPayload) =>
      discountService.upsertDiscount(payload),
    {
      ...errors,
    }
  );

  return {
    upsertCostingCalculation: mutateAsync,
  };
};

const useRemoveCostingCalculations = (
  selectionCount: number,
  position: NotificationPosition = NotificationPosition.TOP
) => {
  const { errors } = useHandleErrors({
    success: { message: t('messageCostingCalculationsRemoveSuccess', { count: selectionCount }) },
    loading: {
      message: t('messageCostingCalculationsRemoveInProgress', { count: selectionCount }),
    },
    error: { title: t('errorTitleCostingCalculationsRemove', { count: selectionCount }) },
    position,
  });

  const { mutateAsync } = useMutation(
    (data: { ids: number[] }) => discountService.removeDiscounts(data),
    {
      ...errors,
    }
  );

  const removeCostingCalculations = (data: { ids: number[] }) => mutateAsync(data);

  return {
    removeCostingCalculations,
  };
};
